import { jsPDF } from 'jspdf';
import { TransactionApiData } from '../../shared/types';

interface ReceiptData {
    doc?: string;
    aut?: string;
    cnpj?: string;
    inscricaoEstadual?: string;
    cidade?: string;
    estado?: string;
    placa?: string;
    valor?: string;
    produto?: string;
    quantidade?: string;
    hodometro?: string;
    cnpjCliente?: string;
    dataHora?: string;
    cartao?: string;
    estabelecimento?: string;
    motorista?: string;
    matricula?: string;
}

export class PdfGenerator {
    private static async loadImageAsBase64(url: string): Promise<string> {
        return new Promise((resolve, reject) => {
            fetch(url)
                .then(response => response.blob())
                .then(blob => {
                    const reader = new FileReader();
                    reader.onloadend = () => {
                        resolve(reader.result as string);
                    };
                    reader.onerror = () => reject(new Error('Error reading image'));
                    reader.readAsDataURL(blob);
                })
                .catch(() => {
                    const img = new Image();
                    img.onload = () => {
                        const canvas = document.createElement('canvas');
                        canvas.width = img.width;
                        canvas.height = img.height;
                        const ctx = canvas.getContext('2d');
                        if (ctx) {
                            ctx.drawImage(img, 0, 0);
                            resolve(canvas.toDataURL('image/png'));
                        } else {
                            reject(new Error('Could not create canvas context'));
                        }
                    };
                    img.onerror = () => reject(new Error('Error loading image'));
                    img.crossOrigin = 'anonymous';
                    img.src = url;
                });
        });
    }

    private static formatCNPJ(cnpj: string): string {
        if (!cnpj) return '';
        const clean = cnpj.replace(/\D/g, '').replace(/^0+/, '');
        if (clean.length === 0) return '0';
        if (clean.length <= 14) {
            return clean.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
        }
        return clean;
    }

    private static extractDataFromReceipt(comprovanteCliente: string): ReceiptData {
        const lines = comprovanteCliente.split('@').map(line => line.trim());
        const data: ReceiptData = {};

        lines.forEach(line => {
            if (line.match(/DOC\s*=\s*(\d+)/i)) {
                const match = line.match(/DOC\s*=\s*(\d+)/i);
                data.doc = match ? match[1] : undefined;
            }
            if (line.match(/AUT\s*=\s*(\d+)/i)) {
                const match = line.match(/AUT\s*=\s*(\d+)/i);
                data.aut = match ? match[1] : undefined;
            }
            if (line.match(/CNPJ:\s*([\d\s]+)/i)) {
                const match = line.match(/CNPJ:\s*([\d\s]+)/i);
                data.cnpj = match ? match[1].trim() : undefined;
            }
            if (line.match(/INSCRICAO ESTADUAL:\s*(\d+)/i)) {
                const match = line.match(/INSCRICAO ESTADUAL:\s*(\d+)/i);
                data.inscricaoEstadual = match ? match[1] : undefined;
            }
            if (line.match(/([A-Z\s]+)\s*-\s*([A-Z]{2})/)) {
                const match = line.match(/([A-Z\s]+)\s*-\s*([A-Z]{2})/);
                if (match && !data.cidade) {
                    data.cidade = match[1].trim();
                    data.estado = match[2];
                }
            }
            if (line.match(/PLACA:\s*([A-Z0-9]+)/i)) {
                const match = line.match(/PLACA:\s*([A-Z0-9]+)/i);
                data.placa = match ? match[1] : undefined;
            }
            if (line.match(/VALOR:\s*R\$\s*([\d,\.]+)/i)) {
                const match = line.match(/VALOR:\s*R\$\s*([\d,\.]+)/i);
                data.valor = match ? match[1] : undefined;
            }
            if (line.match(/Produto:\s*(.+)/i)) {
                const match = line.match(/Produto:\s*(.+)/i);
                data.produto = match ? match[1].trim() : undefined;
            }
            if (line.match(/Qtde:\s*([\d,\.]+)/i)) {
                const match = line.match(/Qtde:\s*([\d,\.]+)/i);
                data.quantidade = match ? match[1] : undefined;
            }
            if (line.match(/KM\s*\/\s*Hr:\s*(\d+)/i)) {
                const match = line.match(/KM\s*\/\s*Hr:\s*(\d+)/i);
                data.hodometro = match ? match[1] : undefined;
            }
            if (line.match(/CNPJ Cliente:\s*(\d+)/i)) {
                const match = line.match(/CNPJ Cliente:\s*(\d+)/i);
                data.cnpjCliente = match ? match[1] : undefined;
            }
            if (line.match(/(\d{2}\/\d{2}\/\d{4}\s+\d{2}:\d{2}:\d{2})/)) {
                const match = line.match(/(\d{2}\/\d{2}\/\d{4}\s+\d{2}:\d{2}:\d{2})/);
                data.dataHora = match ? match[1] : undefined;
            }
            if (line.match(/^\*{4,}\d{4}$/) || line.match(/^\*{8,}\d+/)) {
                data.cartao = line.trim();
            }
            if (line.match(/POSTO\s+(.+)/i) && !data.estabelecimento) {
                const match = line.match(/POSTO\s+(.+)/i);
                data.estabelecimento = match ? match[1].trim() : undefined;
            }
        });

        return data;
    }

    static generateReceiptTextFromTransaction(transaction: TransactionApiData): string {
            return `
            eFleet@@` +
            `CNPJ: ${transaction.mig_red_cnpj_cpf}@` +
            `INSCRICAO ESTADUAL: ${transaction.mig_red_inscricao_estadual}@` +
            `CIDADE: ${transaction.mig_cid_cidade} - ${transaction.mig_est_estado_uf}@` +
            `DOC = ${transaction.mig_tra_id}@` +
            `AUT = ${transaction.mig_tra_autorizacao}@` +
            `${transaction.mig_tra_data_hora}@` +
            `PLACA: ${transaction.mig_vei_placa}@` +
            `VALOR: R$ ${transaction.mig_tra_valor}@` +
            `Produto: ${transaction.mig_pro_produtos}@` +
            `Qtde: ${transaction.mig_tra_quantidade}@` +
            `KM/H: ${transaction.mig_tra_km}@` +  
            `CNPJ Cliente: ${(transaction.cnpjclientenf ? transaction.cnpjclientenf : transaction.mig_cli_cnpj_cpf)}@` +  //se o cnpjClienteNf do cliente for nulo ou vazio, usa o cnpj do mig_cli_cnpj_cpf 
            `Motorista: ${transaction.mig_mot_nome} - ${transaction.mig_mot_matricula}@@` +
            `______________________________@@` +
            `POSTO: ${transaction.mig_red_nome_fantasia}@@`;
    }

    private static renderPage(
        doc: jsPDF,
        receiptData: ReceiptData,
        logoBase64: string | null,
        logoWidth: number,
        logoHeight: number,
        viaLabel?: string
    ): void {
        const pageWidth = doc.internal.pageSize.getWidth();
        const margin = 5;
        let yPosition = margin;
        const lineHeight = 5;
        const sectionSpacing = 3;

        if (viaLabel) {
            doc.setFontSize(8);
            doc.setFont('helvetica', 'bold');
            doc.text(viaLabel, pageWidth / 2, yPosition, { align: 'center' });
            yPosition += lineHeight;
            doc.setLineWidth(0.3);
            doc.line(margin, yPosition, pageWidth - margin, yPosition);
            yPosition += sectionSpacing + 1;
        }

        if (logoBase64 && logoWidth && logoHeight) {
            const logoX = (pageWidth - logoWidth) / 2;
            doc.addImage(logoBase64, 'PNG', logoX, yPosition, logoWidth, logoHeight);
            yPosition += logoHeight + 8;
        }

        doc.setFont('helvetica', 'normal');

        if (receiptData.estabelecimento) {
            doc.setFontSize(9);
            doc.setFont('helvetica', 'bold');
            doc.text(receiptData.estabelecimento, pageWidth / 2, yPosition, { align: 'center' });
            yPosition += lineHeight + 1;
        }

        if (receiptData.cnpj) {
            doc.setFontSize(7);
            doc.setFont('helvetica', 'normal');
            const formattedCNPJ = this.formatCNPJ(receiptData.cnpj);
            doc.text(`CNPJ: ${formattedCNPJ}`, pageWidth / 2, yPosition, { align: 'center' });
            yPosition += lineHeight - 0.5;
        }

        if (receiptData.inscricaoEstadual) {
            doc.setFontSize(7);
            doc.text(`Inscrição Estadual: ${receiptData.inscricaoEstadual}`, pageWidth / 2, yPosition, { align: 'center' });
            yPosition += lineHeight - 0.5;
        }

        if (receiptData.cidade && receiptData.estado) {
            doc.setFontSize(7);
            doc.text(`${receiptData.cidade} - ${receiptData.estado}`, pageWidth / 2, yPosition, { align: 'center' });
            yPosition += lineHeight + sectionSpacing;
        }

        if (receiptData.dataHora) {
            doc.setFontSize(8);
            doc.setFont('helvetica', 'bold');
            doc.text(receiptData.dataHora, pageWidth / 2, yPosition, { align: 'center' });
            yPosition += lineHeight + sectionSpacing;
        }

        const leftColX = margin;
        const rightColX = pageWidth / 2 + 2;

        if (receiptData.doc || receiptData.aut) {
            doc.setFontSize(9);
            doc.setFont('helvetica', 'bold');
            if (receiptData.doc) {
                doc.text(`DOC = ${receiptData.doc}`, leftColX, yPosition);
            }
            if (receiptData.aut) {
                doc.text(`AUT = ${receiptData.aut}`, rightColX, yPosition);
            }
            yPosition += lineHeight + sectionSpacing;
        }

        if (receiptData.placa || receiptData.cartao) {
            doc.setFontSize(8);
            doc.setFont('helvetica', 'normal');
            if (receiptData.placa) {
                doc.text(`Veículo: ${receiptData.placa}`, leftColX, yPosition);
            }
            if (receiptData.cartao) {
                doc.text(receiptData.cartao, rightColX, yPosition);
            }
            yPosition += lineHeight;
        }

        if (receiptData.valor || receiptData.produto) {
            doc.setFontSize(8);
            doc.setFont('helvetica', 'normal');
            if (receiptData.valor) {
                doc.text(`VALOR: R$ ${receiptData.valor}`, leftColX, yPosition);
            }
            if (receiptData.produto) {
                doc.text(`Produto: ${receiptData.produto}`, rightColX, yPosition);
            }
            yPosition += lineHeight;
        }

        if (receiptData.quantidade || receiptData.hodometro) {
            doc.setFontSize(8);
            doc.setFont('helvetica', 'normal');
            if (receiptData.quantidade) {
                doc.text(`Qtde: ${receiptData.quantidade}`, leftColX, yPosition);
            }
            if (receiptData.hodometro) {
                doc.text(`KM/H: ${receiptData.hodometro}`, rightColX, yPosition);
            }
            yPosition += lineHeight + sectionSpacing + 2;
        }

        const lineWidth = 50;
        const lineX = (pageWidth - lineWidth) / 2;
        doc.setLineWidth(0.1);
        doc.line(lineX, yPosition, lineX + lineWidth, yPosition);
        yPosition += lineHeight + sectionSpacing;

        if (receiptData.motorista || receiptData.matricula) {
            doc.setFontSize(8);
            doc.setFont('helvetica', 'normal');
            if (receiptData.motorista) {
                doc.text(`Motorista: ${receiptData.motorista}`, pageWidth / 2, yPosition, { align: 'center' });
                yPosition += lineHeight;
            }
            if (receiptData.matricula) {
                doc.text(`Matrícula: ${receiptData.matricula}`, pageWidth / 2, yPosition, { align: 'center' });
                yPosition += lineHeight;
            }
        }

        if (receiptData.cnpjCliente) {
            doc.setFontSize(7);
            const formattedCNPJCliente = this.formatCNPJ(receiptData.cnpjCliente);
            doc.text(`CNPJ Cliente: ${formattedCNPJCliente}`, pageWidth / 2, yPosition, { align: 'center' });
        }
    }

    static async generateReceipt(
        clientReceipt: string,
        establishmentReceipt?: string | null,
        driverName?: string,
        driverRegistration?: string
    ): Promise<void> {
        if (!clientReceipt) {
            throw new Error('Client receipt not available');
        }

        const doc = new jsPDF({
            orientation: 'portrait',
            unit: 'mm',
            format: [80, 297]
        });

        let logoBase64: string | null = null;
        let logoWidth = 0;
        let logoHeight = 0;

        try {
            logoBase64 = await this.loadImageAsBase64('/images/logo.png');
            const img = new Image();
            img.src = logoBase64;
            await new Promise<void>((resolve) => {
                if (img.complete) {
                    resolve();
                } else {
                    img.onload = () => resolve();
                    img.onerror = () => resolve();
                }
            });

            if (img.width && img.height) {
                const logoMaxWidth = 25;
                const logoMaxHeight = 10;
                const aspectRatio = img.width / img.height;

                logoWidth = logoMaxWidth;
                logoHeight = logoMaxWidth / aspectRatio;

                if (logoHeight > logoMaxHeight) {
                    logoHeight = logoMaxHeight;
                    logoWidth = logoMaxHeight * aspectRatio;
                }
            }
        } catch (error) {
            console.warn('Error loading logo, continuing without it:', error);
        }

        const hasMultipleVias = !!establishmentReceipt;

        if (hasMultipleVias) {
            const estData = this.extractDataFromReceipt(establishmentReceipt!);
            this.renderPage(doc, estData, logoBase64, logoWidth, logoHeight, '1ª VIA - ESTABELECIMENTO');
            doc.addPage();
        }

        const clientData = this.extractDataFromReceipt(clientReceipt);
        if (driverName && driverName !== 'Motorista não identificado') {
            clientData.motorista = driverName;
        }
        if (driverRegistration) {
            clientData.matricula = driverRegistration;
        }
        this.renderPage(doc, clientData, logoBase64, logoWidth, logoHeight, hasMultipleVias ? '2ª VIA - CLIENTE' : undefined);

        const date = new Date();
        const filename = `comprovante_${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, '0')}${String(date.getDate()).padStart(2, '0')}_${String(date.getHours()).padStart(2, '0')}${String(date.getMinutes()).padStart(2, '0')}${String(date.getSeconds()).padStart(2, '0')}.pdf`;

        doc.save(filename);
    }
}
